import React from 'react';
import { useTheme } from 'styled-components';

interface Props extends React.SVGProps<SVGSVGElement> {
  size?: number;
  /** The tier panel's SUMMARY tile draws the same cube brand-gradient filled (frame node 18064:16164). */
  gradient?: boolean;
}

/**
 * CRPD-7274 — the small solid cube that prefixes "These values are calculated from N controls"
 * and labels the "No. of Controls" tile in the tier panel.
 *
 * Solid faces, not an outline: the frame draws a filled isometric cube with a lighter top face
 * and two darker sides, separated by thin seams in the surface colour. The count line uses the
 * grey cube; the SUMMARY tile uses the gradient one.
 */
export const CubeSvg: React.FC<Props> = ({ size, gradient, ...rest }) => {
  const colors = useTheme().colors;
  // useId's own value contains colons, which are not valid inside a `url(#…)` reference.
  const id = `grad${React.useId().replace(/:/g, '')}`;

  if (gradient) {
    return (
      <svg
        width={size ?? 14}
        height={((size ?? 14) * 15.9997) / 14.3752}
        viewBox="0 0 14.3752 15.9997"
        fill="none"
        {...rest}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0 5.42567L6.18129 8.99444C6.24785 9.03287 6.31764 9.06063 6.38869 9.07825V15.9997L0.440851 12.4798C0.167599 12.3181 0 12.0241 0 11.7066L0 5.42567ZM14.3752 5.32753V11.7033C14.3752 12.0208 14.2076 12.3148 13.9343 12.4765L7.98648 15.9964V9.01434C8.00097 9.0071 8.01534 8.99938 8.02955 8.99117L14.3752 5.32753V5.32753Z"
          fill={`url(#${id})`}
        />
        <path
          opacity="0.5"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0.194536 3.69015C0.270038 3.59481 0.365336 3.51422 0.476441 3.45505L6.76531 0.105461C7.02931 -0.0351537 7.34598 -0.0351537 7.60999 0.105461L13.8989 3.45505C13.9845 3.50066 14.0608 3.559 14.1258 3.62701L7.23072 7.60788C7.18538 7.63406 7.14358 7.66401 7.10548 7.69711C7.06739 7.66401 7.02559 7.63406 6.98025 7.60788L0.194536 3.69015Z"
          fill={`url(#${id})`}
        />
        <defs>
          <linearGradient id={id} x1="7.18758" y1="0" x2="7.18758" y2="15.9997" gradientUnits="userSpaceOnUse">
            <stop stopColor={colors.gp1} />
            <stop offset="1" stopColor={colors.gp2} />
          </linearGradient>
        </defs>
      </svg>
    );
  }

  return (
    <svg width={size ?? 18} height={size ?? 18} viewBox="0 0 18 18" fill="none" {...rest} xmlns="http://www.w3.org/2000/svg">
      <path d="M9 1.5L15.5 5.25L9 9L2.5 5.25L9 1.5Z" fill={colors.cflowerBlue} fillOpacity="0.55" />
      <path d="M2.5 5.25L9 9V16.5L2.5 12.75V5.25Z" fill={colors.davysGrey} />
      <path d="M15.5 5.25L9 9V16.5L15.5 12.75V5.25Z" fill={colors.davysGrey} fillOpacity="0.8" />
    </svg>
  );
};
